import React from "react";
import { Button } from "./ui/button";
import { AlertTriangle } from "lucide-react";

/**
 * Iter14 P5.3 — Catches render/runtime errors in any admin/panel subtree so a
 * bad state (e.g. missing settings field) never produces a blank white page.
 * Shows a friendly recovery UI with a "Recharger" button.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }
  handleReload = () => {
    this.setState({ hasError: false, error: null });
    // Force a fresh mount of the subtree by reloading the page — cheapest and
    // most reliable for admin dashboards where state comes from multiple GETs.
    if (typeof window !== "undefined") window.location.reload();
  };
  render() {
    if (this.state.hasError) {
      return (
        <div data-testid="error-boundary-fallback"
             className="p-8 rounded-xl bg-red-50 border border-red-200 text-red-900 max-w-xl mx-auto my-8">
          <div className="flex items-start gap-3">
            <AlertTriangle size={22} className="mt-1 text-red-600"/>
            <div className="flex-1">
              <h3 className="font-semibold text-lg mb-1">Une erreur est survenue</h3>
              <p className="text-sm text-red-800 mb-4">
                La page n'a pas pu se charger correctement. Essayez de recharger.
              </p>
              <Button data-testid="error-boundary-reload"
                      onClick={this.handleReload}
                      className="bg-red-600 hover:bg-red-700 text-white">
                Recharger
              </Button>
              {this.state.error?.message && (
                <p className="text-[11px] text-red-500 mt-3 font-mono">
                  {String(this.state.error.message).slice(0, 200)}
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
